import React, { useLayoutEffect, useMemo, useRef, useState } from "react";

/**
 * Heat bands shared with PlayerVoteSummaryTable, expressed as SVG fill/stroke
 * classes: faint sky for the quietest rounds, then the green deepens toward
 * near-black forest green as the value approaches the top of the scale. Label
 * hues lighten in step so the numerals above the bars carry the same encoding.
 *
 * scaleMax is the value a "maximum" round is worth: 3 for a single player (best
 * on ground), 6 for a whole team (a clean sweep of 3-2-1).
 */
export const bandFor = (v, scaleMax = 3) => {
  if (!v || v <= 0) return null;
  const band = scaleMax / 6;
  if (v < band) return { bar: 'fill-sky-500/25', stroke: 'stroke-sky-500/40', label: 'fill-sky-400' };
  if (v < band * 2) return { bar: 'fill-sky-500/45', stroke: 'stroke-sky-500/50', label: 'fill-sky-300' };
  if (v < band * 3) return { bar: 'fill-emerald-500/80', stroke: 'stroke-emerald-400/40', label: 'fill-emerald-300' };
  if (v < band * 4) return { bar: 'fill-emerald-700', stroke: 'stroke-emerald-500/40', label: 'fill-emerald-200' };
  if (v < band * 5) return { bar: 'fill-emerald-800', stroke: 'stroke-emerald-500/40', label: 'fill-emerald-100' };
  return { bar: 'fill-emerald-900', stroke: 'stroke-emerald-600/60', label: 'fill-emerald-50' };
};

/**
 * Season timeline: one bar per round, vote value on the y axis, coloured with
 * the darkening heat bands above. Same visual language as the dataset bar chart
 * — gridlines, grow-in animation, hover dim — scrolling horizontally when the
 * container is narrower than the round count.
 *
 * values[i] is the vote total for round i.
 */
const RoundVoteBreakdownChart = ({ values = [], scaleMax = 3, ariaLabel = 'Votes by round' }) => {
  const wrapRef = useRef(null);
  const [measuredW, setMeasuredW] = useState(560);
  const [hover, setHover] = useState(null);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => setMeasuredW(Math.max(260, el.clientWidth));
    update();
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(update);
      ro.observe(el);
    } else {
      window.addEventListener('resize', update);
    }
    return () => {
      if (ro) ro.disconnect();
      else window.removeEventListener('resize', update);
    };
  }, []);

  const geom = useMemo(() => {
    const padL = 24;
    const padR = 8;
    const padT = 16;
    const padB = 20;
    const n = values.length;

    // 25 slots don't fit on phones; keep a minimum slot width and let the
    // wrapper scroll horizontally instead of crushing the bars.
    const minSlot = 22;
    const svgW = Math.max(measuredW, padL + padR + n * minSlot);
    const svgH = 170;
    const innerW = svgW - padL - padR;
    const innerH = svgH - padT - padB;
    const slot = n > 0 ? innerW / n : innerW;
    const barW = Math.min(slot * 0.62, 18);

    // Rounds top out at scaleMax, but keep headroom for legacy double-round data.
    const yMax = Math.max(scaleMax, Math.ceil(Math.max(0, ...values)));
    const step = yMax > 6 ? 2 : 1;

    const yFor = (v) => padT + innerH - (Math.max(0, v) / yMax) * innerH;
    const xFor = (i) => padL + slot * i + slot / 2;

    const ticks = [];
    for (let t = 0; t <= yMax; t += step) {
      ticks.push({ v: t, y: yFor(t) });
    }

    return { padL, padR, padT, padB, svgW, svgH, slot, barW, yFor, xFor, ticks };
  }, [values, scaleMax, measuredW]);

  const { padL, padR, padT, padB, svgW, svgH, slot, barW, yFor, xFor, ticks } = geom;
  const baselineY = yFor(0);

  // Changing this remounts the bars so the grow-in animation replays when the
  // subject or the enabled datasets change.
  const animKey = values.map((v) => (v || 0).toFixed(2)).join(',');

  return (
    <div ref={wrapRef} className="relative w-full overflow-x-auto styled-scrollbar">
      <style>{`
        @keyframes roundBarGrow { from { transform: scaleY(0.001); } to { transform: scaleY(1); } }
        .round-bar-grow { transform-box: fill-box; transform-origin: 50% 100%; animation: roundBarGrow 0.5s cubic-bezier(0.22, 1, 0.36, 1) backwards; }
      `}</style>
      <svg width={svgW} height={svgH} className="block" role="img" aria-label={ariaLabel}>
        {/* Gridlines + y labels */}
        {ticks.map((t, i) => (
          <g key={i}>
            <line x1={padL} y1={t.y} x2={svgW - padR} y2={t.y} stroke="#1e293b" strokeWidth="1" />
            <text x={padL - 6} y={t.y + 3} textAnchor="end" className="fill-slate-500" style={{ fontSize: 9 }}>
              {t.v}
            </text>
          </g>
        ))}

        {/* Bars (keyed group so the grow-in animation replays on change) */}
        <g key={animKey}>
          {values.map((value, i) => {
            const v = value || 0;
            const band = bandFor(v, scaleMax);
            const x = xFor(i);
            const y = yFor(v);
            const h = Math.max(0, baselineY - y);
            const dim = hover != null && hover !== i;
            const growDelay = `${Math.min(i * 14, 400)}ms`;
            return (
              <g
                key={i}
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover((h2) => (h2 === i ? null : h2))}
                style={{ cursor: 'default' }}
              >
                <title>{`Round ${i}: ${v.toFixed(2)} votes`}</title>
                {/* Hover hit area */}
                <rect x={x - slot / 2} y={padT} width={slot} height={svgH - padT - padB} fill="transparent" />
                {band ? (
                  <rect
                    x={x - barW / 2}
                    y={y}
                    width={barW}
                    height={h}
                    rx={2.5}
                    className={`round-bar-grow ${band.bar} ${band.stroke}`}
                    strokeWidth="1"
                    opacity={dim ? 0.45 : 1}
                    style={{ transition: 'opacity 0.12s', animationDelay: growDelay }}
                  />
                ) : (
                  // Vote-less round: a recessed stub keeps the timeline continuous.
                  <rect x={x - barW / 2} y={baselineY - 2.5} width={barW} height={2.5} rx={1.25} className="fill-slate-700" opacity={dim ? 0.4 : 0.7} />
                )}
                {/* Value label above the bar (zeros stay unlabelled) */}
                {band && (
                  <text
                    x={x}
                    y={y - 4}
                    textAnchor="middle"
                    className={band.label}
                    style={{ fontSize: 8, fontWeight: 700, pointerEvents: 'none' }}
                    opacity={dim ? 0.5 : 1}
                  >
                    {v.toFixed(2)}
                  </text>
                )}
                {/* Round label */}
                <text
                  x={x}
                  y={svgH - 6}
                  textAnchor="middle"
                  className={hover === i ? 'fill-slate-300' : 'fill-slate-500'}
                  style={{ fontSize: 8 }}
                >
                  {i}
                </text>
              </g>
            );
          })}
        </g>

        {/* Baseline */}
        <line x1={padL} y1={baselineY} x2={svgW - padR} y2={baselineY} stroke="#334155" strokeWidth="1.25" />
      </svg>
    </div>
  );
};

export default RoundVoteBreakdownChart;
